/**
 * Organizasyon şeması grafiği: `department.parent` ağacı (K3) flowy düğümlerine çevrilir.
 * Saf — store'a, Vue'ya ve flowy'ye bağımlı değil, bu yüzden testi mount gerektirmez.
 *
 * Eski `unit` grafiğinin düğüm şekli korunur: organizasyon tek kök ({{parentId: ROOT_PARENT_ID}}),
 * birimler onun altında. Üst birim modülü TKP-27'de kalktığı için kutular artık yalnız birim.
 * Döngü/silinmiş üst dayanıklılığı `buildDepartmentTree`'den miras alınır (K3).
 */
import { ancestorsOf, buildDepartmentTree, subtreeIds } from "./tree";

export const ROOT_PARENT_ID = -1;
export const ORGANIZATION_ICON = "bx bxs-school";
export const DEPARTMENT_ICON = "mdi mdi-storefront-outline";

const idOf = (ref) => (ref && ref._id ? String(ref._id) : ref ? String(ref) : undefined);

const HTML_ESCAPES = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
/** `graph-node` açıklamayı `v-html` ile basıyor; birim ve yetkili adları kullanıcı verisi, kaçırılmadan basılmaz. */
export const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);

/** TKP-80: birimin yetkili adları, sırası bozulmadan. */
export const adminNames = (doc = {}) => (doc.admins ?? []).map((admin) => admin && admin.account && admin.account.name).filter(Boolean);

/**
 * Kutunun altındaki satır: TKP-64 "süreç eklenebilir" rozeti ve TKP-80 yetkilileri (ilk yetkili + kalan sayısı).
 * `labels` çeviri metinleri — saf kalsın diye dışarıdan verilir.
 */
export const nodeDescription = (doc, labels = {}) => {
  const parts = [];
  if (doc.processAllowed !== false && labels.processAllowed) {
    parts.push(`<span class="badge badge-soft-success">${escapeHtml(labels.processAllowed)}</span>`);
  }
  const admins = adminNames(doc);
  if (admins.length) {
    const extra = admins.length > 1 ? ` +${admins.length - 1}` : "";
    // TKP-80: ilk yetkili + kalan sayısı; tam liste ipucu (`title`) olarak durur
    parts.push(`<div class="text-muted mt-1" title="${escapeHtml(admins.join(", "))}"><i class="bx bx-user"></i> ${escapeHtml(admins[0])}${escapeHtml(extra)}</div>`);
  }
  return parts.join("");
};

/**
 * İki açılır süzgeç: üst birim ve birim. Her biri seçilen birimin ALT AĞACINI ve ÜST ZİNCİRİNİ bırakır —
 * üst zincir olmadan kutu organizasyon köküne kopuk bağlanır ve şema yanıltır. İkisi birlikte verilirse kesişim alınır.
 */
export const visibleDocs = (docs = [], { parentId, departmentId } = {}, label = (d) => d.name) => {
  const { byId } = buildDepartmentTree(docs, label);
  const keepFor = (id) => {
    const keep = subtreeIds(id, byId);
    ancestorsOf(id, byId).forEach((a) => keep.add(String(a._id)));
    return keep;
  };
  let allowed;
  for (const id of [parentId, departmentId].filter(Boolean)) {
    if (!byId.has(String(id))) continue;
    const keep = keepFor(String(id));
    allowed = allowed ? new Set([...allowed].filter((x) => keep.has(x))) : keep;
  }
  return allowed ? docs.filter((d) => allowed.has(String(d._id))) : docs;
};

/**
 * Flowy düğüm listesi. Organizasyon her zaman tek köktür; süzgeçten sonra üstü görünmeyen birim köke bağlanır.
 * `label` çeviri filtresi, `labels` kutu içi metinler.
 */
export const buildGraphNodes = (docs = [], { organization, label = (d) => d.name, labels = {}, parentId, departmentId } = {}) => {
  if (!organization || !organization._id) return [];
  const visible = visibleDocs(docs, { parentId, departmentId }, label);
  const { roots } = buildDepartmentTree(visible, label);

  const nodes = [
    {
      id: String(organization._id),
      parentId: ROOT_PARENT_ID,
      nodeComponent: "graph-node",
      icon: ORGANIZATION_ICON,
      root: true,
      data: { text: label(organization), title: label(organization), description: null },
    },
  ];

  const walk = (node, parent) => {
    const { children, ...entity } = node;
    nodes.push({
      id: String(node._id),
      parentId: parent,
      nodeComponent: "graph-node",
      icon: DEPARTMENT_ICON,
      type: "Department",
      data: { text: label(node), title: label(node), description: nodeDescription(node, labels) },
      entity,
    });
    children.forEach((child) => walk(child, String(node._id)));
  };
  roots.forEach((root) => walk(root, String(organization._id)));

  return nodes;
};

/** Sürüklenen kutu hedefin altına bırakılabilir mi: kendi alt ağacına taşınamaz (K3 döngü koruması). */
export const canMoveUnder = (nodes = [], draggedId, targetId) => {
  if (!draggedId || !targetId) return false;
  if (String(draggedId) === String(targetId)) return false;
  const byId = new Map(nodes.map((n) => [String(n.id), n]));
  let cursor = byId.get(String(targetId));
  let guard = 0;
  while (cursor && guard++ < 1000) {
    if (String(cursor.id) === String(draggedId)) return false;
    cursor = cursor.parentId === ROOT_PARENT_ID ? undefined : byId.get(String(cursor.parentId));
  }
  return true;
};

export { idOf };
